/**
 * The people who work in this CRM: `/admin/crm/people`.
 *
 * Its own file and its own type rather than a widening of `StaffUser`
 * (`lib/staff.ts`), and the distinction is worth keeping. `StaffUser` answers
 * "who can own work" for every picker in the app and is readable by anybody
 * with `view:crm`. This answers "who works here and what may they do", carries
 * a role and an invitation state, and is behind `manage:roles`. Merging them
 * would put a role and a pending-invitation flag into six pickers that have no
 * business drawing either - and would make the wider question need the narrower
 * right.
 */
import { api } from "./api.js";

export interface CrmPerson {
  id: string;
  name: string;
  email: string;
  avatarUrl: string | null;
  /** Membership AND account both open. Either one off means "not here". */
  active: boolean;
  roleId: string | null;
  roleName: string | null;
  /** Never set a password: the invitation has not been accepted yet. */
  pending: boolean;
  createdAt: string;
}

export interface CrmRoleOption {
  id: string;
  name: string;
}

export interface CrmPeopleResponse {
  rows: CrmPerson[];
  roles: CrmRoleOption[];
}

/** What the server did with the "set your password" mail, if it was asked to. */
export type InviteOutcome = "sent" | "throttled" | "already_has_password" | null;

export const getCrmPeople = () => api.get<CrmPeopleResponse>("/admin/crm/people");

/**
 * `reused: true` means the address already had a platform account and was
 * seated here rather than created. Not an error - somebody may hold a customer
 * account on the same address - but the screen should say so, because "added"
 * and "found and added" are different facts about a colleague.
 */
export const createCrmPerson = (body: {
  name: string;
  email: string;
  roleId: string;
  invite: boolean;
  locale?: string;
}) => api.post<{ id: string; reused: boolean; invited: InviteOutcome }>("/admin/crm/people", body);

export const updateCrmPerson = (
  id: string,
  body: { name?: string; roleId?: string; active?: boolean },
) => api.patch<{ ok: true }>(`/admin/crm/people/${id}`, body);

export const inviteCrmPerson = (id: string, locale?: string) =>
  api.post<{ ok: true; sent: boolean; sentTo: string }>(`/admin/crm/people/${id}/invite`, { locale });
